import { useMemo } from 'react';
import { LayoutGrid } from '@/components/layout';
import { Button } from '@/components/ui';
import { ApiError } from '@/lib/api';
import type { FabColumn, FabFilters, FabOption, FabSummary, FabSchoolRow } from '@/lib/types';
import type { LayoutBlockDefault, PageLayout } from '@/lib/use-page-layout';
import { BarList, FunnelChart, type BarListRow, type FunnelStage } from './fab-ui';
import type { FabOverviewData } from './use-fab-data';
import type { FabStage } from './fab-filter-state';
import { PiEmpty, PiError, PiSection, PiStat, PiStatGrid, piCount, piPct } from './pi-kit';

/**
 * SEED FAB Overview tab, top-down: headline tiles, the funnel, then breakdowns.
 *
 * - Tiles carry figures the funnel does NOT show (no figure twice on screen):
 *   schools with applications, how many of them are linked to a SEED EMERGE
 *   company, scholarships with applications, and regions (or countries).
 * - Funnel: Scholarship applications (scholarships totals.total), then School
 *   applications / Admits / Enrolled (each school stage's totals.total).
 *   FunnelChart draws the conversion pills between stages.
 * - Breakdowns all read the APPLICATIONS school stage, except Top
 *   scholarships (scholarships summary).
 * - By region: summary columns are positional (`r0`, `c0`…), so a column is
 *   identified by `regionId` / `countryId` (falling back to its label), never by
 *   `key`. The key is only used to read `totals.values` of the SAME response.
 *   Columns that share a region (intake split) are summed into one row.
 * - States: skeletons while the first load runs; PiError when the load failed
 *   and nothing is cached; PiEmpty when every stage is 0.
 * - Layout manager (2026-10-20): the tiles stay fixed; the funnel and the five
 *   bar lists are movable blocks of `layout` (page id
 *   `product-insights.fab.overview`, declared by the page with
 *   FAB_OVERVIEW_LAYOUT). The defaults reproduce the fixed layout: the funnel
 *   full width, the bar lists half width in a two-column grid at `lg`.
 */

/** Block ids are stored per person: never rename one. */
export const FAB_OVERVIEW_LAYOUT: readonly LayoutBlockDefault[] = [
  { id: 'funnel', label: 'Funnel' },
  { id: 'by-geography', label: 'By region', width: 'half' },
  { id: 'by-source', label: 'By source', width: 'half' },
  { id: 'by-first-product', label: 'By first product', width: 'half' },
  { id: 'top-schools', label: 'Top schools', width: 'half' },
  { id: 'top-scholarships', label: 'Top scholarships', width: 'half' },
];

export interface FabOverviewProps {
  layout: PageLayout;
  data: FabOverviewData | null;
  loading: boolean;
  error: unknown | null;
  filters: FabFilters | null;
  onOpenSchools(stage: FabStage): void;
  onOpenScholarships(search?: string): void;
  onOpenSchool(name: string): void;
}

const TOP_ROWS = 8;

function columnIdentity(column: FabColumn): { key: string; kind: 'region' | 'country' | 'other' } {
  if (column.regionId !== undefined) return { key: `region-${column.regionId}`, kind: 'region' };
  if (column.countryId !== undefined) return { key: `country-${column.countryId}`, kind: 'country' };
  return { key: `label-${column.label}`, kind: 'other' };
}

function geographyRows(summary: FabSummary<FabSchoolRow>): { rows: BarListRow[]; byCountry: boolean } {
  const grouped = new Map<string, { label: string; value: number; kind: 'region' | 'country' | 'other' }>();
  for (const column of summary.columns) {
    if (column.regionId === undefined && column.countryId === undefined) continue;
    const identity = columnIdentity(column);
    const value = summary.totals.values[column.key] ?? 0;
    const existing = grouped.get(identity.key);
    if (existing) existing.value += value;
    else grouped.set(identity.key, { label: column.label, value, kind: identity.kind });
  }
  const entries = [...grouped.entries()];
  const byCountry = entries.length > 0 && entries.every(([, entry]) => entry.kind === 'country');
  const rows = entries
    .filter(([, entry]) => entry.value > 0)
    .map(([key, entry]) => ({ key, label: entry.label, value: entry.value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
  return { rows, byCountry };
}

function optionRows(counts: Record<string, number> | undefined, options: FabOption[] | undefined): BarListRow[] {
  if (!counts) return [];
  const labels = new Map((options ?? []).map((option) => [option.value, option.label]));
  return Object.entries(counts)
    .filter(([, value]) => value > 0)
    .map(([value, count]) => ({ key: value, label: labels.get(value) ?? value, value: count }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

function errorText(error: unknown): string {
  if (error instanceof ApiError && error.message) return error.message;
  return 'Funnel Analytics & BI could not be reached.';
}

function ViewAll({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button variant="ghost" size="sm" onClick={onClick}>
      {label}
    </Button>
  );
}

export function FabOverview({
  layout,
  data,
  loading,
  error,
  filters,
  onOpenSchools,
  onOpenScholarships,
  onOpenSchool,
}: FabOverviewProps) {
  const showSkeleton = loading && data === null;

  const model = useMemo(() => {
    if (!data) return null;
    const applications = data.schools.applications;
    const geography = geographyRows(applications);
    const sources = optionRows(applications.totals.sources, filters?.sources);
    const products = optionRows(applications.totals.products, filters?.products);
    const appliedSchools = data.merged.filter((row) => row.applications > 0);
    const linkedSchools = appliedSchools.filter((row) => row.company !== null).length;
    const appliedScholarships = data.scholarships.rows.filter((row) => row.total > 0);
    const topSchools: BarListRow[] = appliedSchools.slice(0, TOP_ROWS).map((row) => ({
      key: row.schoolId,
      label: row.displayName,
      value: row.applications,
      hint: row.country ?? undefined,
      onSelect: () => onOpenSchool(row.displayName),
    }));
    const topScholarships: BarListRow[] = [...appliedScholarships]
      .sort((a, b) => b.total - a.total || a.displayName.localeCompare(b.displayName))
      .slice(0, TOP_ROWS)
      .map((row) => ({
        key: row.scholarshipId,
        label: row.displayName,
        value: row.total,
        onSelect: () => onOpenScholarships(row.displayName),
      }));
    const isEmpty =
      data.scholarships.totals.total === 0 &&
      applications.totals.total === 0 &&
      data.schools.admits.totals.total === 0 &&
      data.schools.enrolled.totals.total === 0;
    return {
      applicationsTotal: applications.totals.total,
      scholarshipsTotal: data.scholarships.totals.total,
      schoolCount: appliedSchools.length,
      linkedSchools,
      scholarshipCount: appliedScholarships.length,
      geography,
      sources,
      products,
      topSchools,
      topScholarships,
      isEmpty,
    };
  }, [data, filters, onOpenSchool, onOpenScholarships]);

  if (!showSkeleton && !data) {
    if (error !== null && error !== undefined && !loading) return <PiError message={errorText(error)} />;
    return null;
  }

  const openSchools = () => onOpenSchools('applications');
  const openScholarships = () => onOpenScholarships();

  const stages: FunnelStage[] = [
    {
      key: 'scholarship-applications',
      label: 'Scholarship applications',
      value: data ? data.scholarships.totals.total : null,
      onSelect: openScholarships,
    },
    {
      key: 'applications',
      label: 'School applications',
      value: data ? data.schools.applications.totals.total : null,
      onSelect: openSchools,
    },
    {
      key: 'admits',
      label: 'Admits',
      value: data ? data.schools.admits.totals.total : null,
      onSelect: () => onOpenSchools('admits'),
    },
    {
      key: 'enrolled',
      label: 'Enrolled',
      value: data ? data.schools.enrolled.totals.total : null,
      onSelect: () => onOpenSchools('enrolled'),
    },
  ];

  const geographyLabel = model?.geography.byCountry ? 'Countries' : 'Regions';

  return (
    <div className="grid min-w-0 gap-4">
      <PiStatGrid>
        <PiStat
          label="Schools"
          value={piCount(model?.schoolCount)}
          onClick={openSchools}
          loading={showSkeleton}
        />
        <PiStat
          label="Linked to SEED EMERGE"
          value={piCount(model?.linkedSchools)}
          detail={
            model && model.schoolCount > 0
              ? `${piPct(model.linkedSchools / model.schoolCount)} of schools`
              : undefined
          }
          loading={showSkeleton}
        />
        <PiStat
          label="Scholarships"
          value={piCount(model?.scholarshipCount)}
          onClick={openScholarships}
          loading={showSkeleton}
        />
        <PiStat
          label={geographyLabel}
          value={piCount(model?.geography.rows.length)}
          loading={showSkeleton}
        />
      </PiStatGrid>

      {model?.isEmpty ? (
        <PiEmpty
          title="No applications"
          description="Nothing matches these filters in this cycle. Try another cycle or clear the filters."
        />
      ) : (
        <LayoutGrid
          layout={layout}
          render={{
            funnel: () => (
              <PiSection title="Funnel">
                <FunnelChart stages={stages} loading={showSkeleton} />
              </PiSection>
            ),
            'by-geography': () => (
              <BarList
                title={model?.geography.byCountry ? 'By country' : 'By region'}
                total={model ? model.applicationsTotal : null}
                rows={model?.geography.rows ?? []}
                loading={showSkeleton}
                emptyText="No school applications for these filters."
                footer={<ViewAll label="View all schools" onClick={openSchools} />}
              />
            ),
            'by-source': () => (
              <BarList
                title="By source"
                total={model ? model.applicationsTotal : null}
                rows={model?.sources ?? []}
                loading={showSkeleton}
                emptyText="No school applications for these filters."
                footer={<ViewAll label="View all schools" onClick={openSchools} />}
              />
            ),
            'by-first-product': () => (
              <BarList
                title="By first product"
                total={model ? model.applicationsTotal : null}
                rows={model?.products ?? []}
                loading={showSkeleton}
                emptyText="No school applications for these filters."
                footer={<ViewAll label="View all schools" onClick={openSchools} />}
              />
            ),
            'top-schools': () => (
              <BarList
                title="Top schools"
                total={model ? model.applicationsTotal : null}
                rows={model?.topSchools ?? []}
                loading={showSkeleton}
                maxRows={TOP_ROWS}
                emptyText="No school applications for these filters."
                footer={<ViewAll label="View all schools" onClick={openSchools} />}
              />
            ),
            'top-scholarships': () => (
              <BarList
                title="Top scholarships"
                total={model ? model.scholarshipsTotal : null}
                rows={model?.topScholarships ?? []}
                loading={showSkeleton}
                maxRows={TOP_ROWS}
                emptyText="No scholarship applications for these filters."
                footer={<ViewAll label="View all scholarships" onClick={openScholarships} />}
              />
            ),
          }}
        />
      )}
    </div>
  );
}
